import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  Box,
  Pagination,
  TextFilter,
  CollectionPreferences,
  SpaceBetween,
  Button,
  DateRangePicker,
  FormField,
} from '@amzn/awsui-components-react/polaris';
import PropTypes from 'prop-types';
import { getDeploymentColumns, VISIBLE_CONTENT_OPTIONS } from './columns';
import { i18nStrings } from '../../../../Domain/KeyEvents/utils';
import { useSdpdsReadOnlyOperation } from '../../../hooks/useSdpdsReadOnlyOperation';

const DeploymentFetcher = ({ payload, onSuccess, onError }) => {
  const { data, error } = useSdpdsReadOnlyOperation({
    operation_name: 'ListCustomerDeploymentConfigs',
    payload
  });

  const processedRef = useRef(false);

  useEffect(() => {
    if (processedRef.current) return;

    if (error) {
      processedRef.current = true;
      onError(error);
    } else if (data?.sdpdsReadOnlyOperation) {
      processedRef.current = true;
      onSuccess(data.sdpdsReadOnlyOperation);
    }
  }, [data, error, onSuccess, onError]);

  return null;
};

export const DeploymentDetailsTable = ({ stackName, releaseVersion, status }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [preferences, setPreferences] = useState({
    pageSize: 20,
    visibleContent: ['clusterId', 'status', 'requestSource', 'deploymentId', 'creationTime', 'lastModificationTime', 'deploymentType', 'notificationDetails', 'stackName', 'releaseVersion']
  });
  const [filteringText, setFilteringText] = useState('');
  const [dateRange, setDateRange] = useState(undefined);
  const tableRef = useRef(null);
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [nextToken, setNextToken] = useState(null);
  const [allData, setAllData] = useState([]);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [fetchPayload, setFetchPayload] = useState(null);
  const [fetchKey, setFetchKey] = useState(0);

  const columnDefinitions = getDeploymentColumns();

  const getPayload = (token = null, range = dateRange) => {
    const payload = {
      stackName,
      releaseVersion,
      status,
      maxResults: 50,
      ...(token && { nextToken: token })
    };

    if (range) {
      if (range.type === 'absolute') {
        payload.updateStartTime = Math.floor(new Date(range.startDate).getTime() / 1000);
        payload.updateEndTime = Math.floor(new Date(range.endDate).getTime() / 1000);
      } else if (range.type === 'relative') {
        const now = new Date();
        const startTime = new Date();

        switch (range.unit) {
          case 'minute':
            startTime.setMinutes(startTime.getMinutes() - range.amount);
            break;
          case 'hour':
            startTime.setHours(startTime.getHours() - range.amount);
            break;
          default:
            break;
        }

        payload.updateStartTime = Math.floor(startTime.getTime() / 1000);
        payload.updateEndTime = Math.floor(now.getTime() / 1000);
      }
    }

    return payload;
  };

  const handleFetchSuccess = (response) => {
    const newData = response.customerDeploymentConfigsList || [];

    setAllData(prevData => {
      const currentData = Array.isArray(prevData) ? prevData : [];
      return [...currentData, ...newData];
    });

    if (response.nextToken) {
      setNextToken(response.nextToken);
      setFetchPayload(getPayload(response.nextToken));
      setFetchKey(prev => prev + 1);
    } else {
      setNextToken(null);
      setFetchPayload(null);
      setIsLoading(false);
      setIsPaginationLoading(false);
    }

    setError(null);
  };

  const handleFetchError = (err) => {
    console.error('Error fetching deployment details:', err);
    setError('Failed to fetch deployment details');
    setIsLoading(false);
    setIsPaginationLoading(false);
    setFetchPayload(null);
  };

  const startFetch = (token = null, range = dateRange) => {
    setIsPaginationLoading(true);
    setIsLoading(true);
    setFetchPayload(getPayload(token, range));
    setFetchKey(prev => prev + 1);
  };

  const resetAndFetch = (newDateRange = null) => {
    setDateRange(newDateRange);
    setCurrentPageIndex(1);
    setAllData([]);
    setNextToken(null);
    startFetch(null, newDateRange);
  };

  useEffect(() => {
    if (status) {
      resetAndFetch(dateRange);
    }
  }, [status]);

  const getCurrentPageItems = () => {
    if (isPaginationLoading) {
      const start = (currentPageIndex - 1) * preferences.pageSize;
      const end = start + preferences.pageSize;
      return allData.slice(start - preferences.pageSize, end - preferences.pageSize);
    }

    const filteredItems = getFilteredItems();
    const start = (currentPageIndex - 1) * preferences.pageSize;
    const end = start + preferences.pageSize;
    return filteredItems.slice(start, end);
  };

  const handlePageChange = ({ detail }) => {
    if (isPaginationLoading) return;

    const newPageIndex = detail.currentPageIndex;
    setCurrentPageIndex(newPageIndex);

    const totalItemsFetched = allData.length;
    const requiredItems = newPageIndex * preferences.pageSize;

    if (requiredItems > totalItemsFetched && nextToken) {
      startFetch(nextToken);
    }
  };

  const getFilteredItems = () => {
    if (!filteringText) return allData;

    const searchText = filteringText.toLowerCase();
    return allData.filter(item =>
      Object.values(item).some(value =>
        String(value).toLowerCase().includes(searchText)
      )
    );
  };

  const getDateRangeText = () => {
    if (!dateRange) return '';
    if (dateRange.type === 'absolute') {
      return `${new Date(dateRange.startDate).toLocaleDateString()} - ${new Date(dateRange.endDate).toLocaleDateString()}`;
    }
    return `Last ${dateRange.amount} ${dateRange.unit}${dateRange.amount > 1 ? 's' : ''}`;
  };

  if (error) {
    return <Box textAlign="center" color="error">{error}</Box>;
  }

  return (
    <div ref={tableRef}>
      {fetchPayload && (
        <DeploymentFetcher
          key={fetchKey}
          payload={fetchPayload}
          onSuccess={handleFetchSuccess}
          onError={handleFetchError}
        />
      )}

      <Table
        columnDefinitions={columnDefinitions}
        items={getCurrentPageItems()}
        loading={isLoading || isPaginationLoading}
        loadingText="Loading clusters..."
        header={
          <SpaceBetween size="m">
            <div>{`Clusters in ${status} status (${allData.length})`}</div>
            <SpaceBetween size="m" direction="horizontal" alignItems="center">
              <FormField stretch={true}>
                <DateRangePicker
                  onChange={({ detail }) => resetAndFetch(detail.value)}
                  value={dateRange}
                  showClearButton
                  relativeOptions={[
                    {
                      key: "previous-5-minutes",
                      amount: 5,
                      unit: "minute",
                      type: "relative"
                    },
                    {
                      key: "previous-30-minutes",
                      amount: 30,
                      unit: "minute",
                      type: "relative"
                    },
                    {
                      key: "previous-1-hour",
                      amount: 1,
                      unit: "hour",
                      type: "relative"
                    },
                    {
                      key: "previous-6-hours",
                      amount: 6,
                      unit: "hour",
                      type: "relative"
                    }
                  ]}
                  isValidRange={range => {
                    if (range?.type === "absolute") {
                      const [startDateWithoutTime] = range.startDate.split("T");
                      const [endDateWithoutTime] = range.endDate.split("T");

                      if (!startDateWithoutTime || !endDateWithoutTime) {
                        return {
                          valid: false,
                          errorMessage: "The selected date range is incomplete. Select a start and end date for the date range."
                        };
                      }

                      if (new Date(range.startDate) - new Date(range.endDate) > 0) {
                        return {
                          valid: false,
                          errorMessage: "The selected date range is invalid. The start date must be before the end date."
                        };
                      }
                    }
                    return { valid: true };
                  }}
                  i18nStrings={i18nStrings}
                  placeholder="Filter by a date and time range"
                />
              </FormField>
              {dateRange && (
                <Button onClick={() => resetAndFetch(null)}>
                  Reset Filter
                </Button>
              )}
            </SpaceBetween>
            {dateRange && (
              <Box color="text-status-info" fontSize="body-s">
                Filtered by date range: {getDateRangeText()}
              </Box>
            )}
          </SpaceBetween>
        }
        filter={
          <TextFilter
            filteringPlaceholder="Find clusters"
            filteringText={filteringText}
            onChange={({ detail }) => {
              setFilteringText(detail.filteringText);
              setCurrentPageIndex(1);
            }}
            countText={`${getFilteredItems().length} matches`}
          />
        }
        preferences={
          <CollectionPreferences
            title="Preferences"
            confirmLabel="Confirm"
            cancelLabel="Cancel"
            preferences={preferences}
            pageSizePreference={{
              title: "Page size",
              options: [
                { value: 10, label: "10 clusters" },
                { value: 20, label: "20 clusters" },
                { value: 50, label: "50 clusters" }
              ]
            }}
            visibleContentPreference={{
              title: 'Column preferences',
              options: [
                {
                  label: 'Main attributes',
                  options: VISIBLE_CONTENT_OPTIONS
                }
              ]
            }}
            onConfirm={({ detail }) => setPreferences(detail)}
          />
        }
        visibleColumns={preferences.visibleContent}
        empty={
          <Box textAlign="center" color="inherit">
            <b>No data available</b>
            <Box variant="p" color="inherit">
              No clusters found in {status} status
            </Box>
          </Box>
        }
        variant="embedded"
        stickyHeader
        pagination={
          <Pagination
            currentPageIndex={currentPageIndex}
            onChange={handlePageChange}
            pagesCount={Math.ceil(getFilteredItems().length / preferences.pageSize) + (nextToken ? 1 : 0)}
            pageSize={preferences.pageSize}
            disabled={isPaginationLoading}
          />
        }
      />
    </div>
  );
};

DeploymentDetailsTable.propTypes = {
  stackName: PropTypes.string.isRequired,
  releaseVersion: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  onClose: PropTypes.func
};